import React from 'react';
import { CalendarDays, CheckCircle2, Loader2, XCircle, MinusCircle } from 'lucide-react';
import type {
  TeamMemberAvailabilityStatus,
  TeamMemberAvailabilityVM,
} from '../../business/availability/TeamMemberAvailabilityManager';

interface Props {
  members: TeamMemberAvailabilityVM[];
  selectedUserId: string | null;
  loading: boolean;
  onSelect: (userId: string) => void;
  /** Members outside the customer's zip territory; they get an "Outside territory" chip but stay selectable. */
  outsideTerritoryUserIds?: ReadonlySet<string>;
}

const STATUS_META: Record<
  TeamMemberAvailabilityStatus,
  { label: string; pill: string; icon: React.ReactNode }
> = {
  Available: {
    label: 'Available',
    pill: 'bg-success-50 text-success-700 ring-1 ring-success-200',
    icon: <CheckCircle2 className="h-3.5 w-3.5 text-success-600" />,
  },
  Unavailable: {
    label: 'Unavailable',
    pill: 'bg-danger-50 text-danger-700 ring-1 ring-danger-200',
    icon: <XCircle className="h-3.5 w-3.5 text-danger-600" />,
  },
  NotWorking: {
    label: 'Not working',
    pill: 'bg-ink-100 text-ink-600 ring-1 ring-ink-200',
    icon: <MinusCircle className="h-3.5 w-3.5 text-ink-500" />,
  },
};

/**
 * Horizontal, wrapping row of team-member availability cards for the exact-time picker.
 * Each card shows the member's name, a status pill (Available / Unavailable / Not working),
 * the day's appointment load ("X / Y appointments" and "X of Y scheduled") and a radio that
 * selects the member without leaving the booking flow. Purely presentational.
 */
export const TeamMemberAvailabilityCards: React.FC<Props> = ({
  members,
  selectedUserId,
  loading,
  onSelect,
  outsideTerritoryUserIds,
}) => {
  if (!loading && members.length === 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[10.5px] font-semibold uppercase tracking-wider text-ink-500">
          Team member availability
        </p>
        {loading && (
          <span className="inline-flex items-center gap-1 text-[11px] text-ink-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Checking…
          </span>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-0.5 px-0.5">
        {members.map((member) => {
          const meta = STATUS_META[member.status];
          const isSelected = selectedUserId === member.userId;
          const capacityLabel =
            member.dailyCapacity != null
              ? `${member.appointmentCount} / ${member.dailyCapacity}`
              : `${member.appointmentCount}`;

          return (
            // eslint-disable-next-line no-restricted-syntax -- selectable availability card (radio option); dedicated primitive pending
            <button
              key={member.userId}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(member.userId)}
              className={`flex-shrink-0 w-[168px] text-left rounded-lg border px-3 py-2.5 transition-colors ${
                isSelected
                  ? 'border-success-300 bg-success-50/60 ring-1 ring-success-200'
                  : 'border-ink-200 bg-surface hover:bg-ink-50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-[13px] font-semibold text-ink-900 leading-tight truncate">
                  {member.name}
                </span>
                <span
                  className={`mt-0.5 inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full border ${
                    isSelected ? 'border-success-500 bg-success-500' : 'border-ink-300 bg-surface'
                  }`}
                  aria-hidden="true"
                >
                  {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                </span>
              </div>

              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${meta.pill}`}
                >
                  {meta.icon}
                  {meta.label}
                </span>
                {outsideTerritoryUserIds?.has(member.userId) && (
                  <span
                    className="inline-flex items-center rounded-full bg-warning-50 px-1.5 py-0.5 text-2xs font-semibold text-warning-700 ring-1 ring-warning-200"
                    title="Not assigned to the customer's zip territory. You can still book them as an override."
                  >
                    Outside territory
                  </span>
                )}
                {member.autoAssignRank != null && (
                  <span className="inline-flex items-center rounded-full bg-ink-100 px-1.5 py-0.5 text-[10px] font-semibold text-ink-600 ring-1 ring-ink-200">
                    #{member.autoAssignRank}
                  </span>
                )}
              </div>

              <p className="mt-1.5 text-[12px] font-semibold text-ink-800 tabular-nums">
                {capacityLabel} appointments
              </p>
              {member.dailyCapacity != null && (
                <p className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] text-ink-500">
                  <CalendarDays className="h-3 w-3" />
                  {member.appointmentCount} of {member.dailyCapacity} scheduled
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TeamMemberAvailabilityCards;
